import { useState, useEffect } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useApiClient } from './useApiClient';
import { envConfig } from '../config/authConfig';

const API_BASE_URL = envConfig.apiBaseUrl;

interface AdminStatus {
  isAdmin: boolean;
  email: string | null;
  loading: boolean;
}

/**
 * Returns whether the currently signed-in user is in the Admins:Emails allowlist.
 * Used by the Sidebar to show/hide the Admin nav link, and by guarded routes.
 *
 * Status is fetched once after authentication completes and cached for the session.
 */
export const useIsAdmin = (): AdminStatus => {
  const isAuthenticated = useIsAuthenticated();
  const { apiCall } = useApiClient();
  const [status, setStatus] = useState<AdminStatus>({
    isAdmin: false,
    email: null,
    loading: true
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus({ isAdmin: false, email: null, loading: false });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE_URL}/api/Admin/me`);
        if (cancelled) return;
        if (resp.ok) {
          const json = await resp.json();
          setStatus({
            isAdmin: Boolean(json?.data?.isAdmin),
            email: json?.data?.email ?? null,
            loading: false
          });
        } else {
          setStatus({ isAdmin: false, email: null, loading: false });
        }
      } catch {
        if (!cancelled) setStatus({ isAdmin: false, email: null, loading: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, apiCall]);

  return status;
};
